import { Dialog, VisuallyHidden } from "radix-ui";
import { useState } from "react";

import { usePanelWidth } from "./SideThreadPanel";
import panelStyles from "./SideThreadPanel.module.css";
import styles from "./SideThreadsList.module.css";
import { Button } from "../../components/Button";
import {
  type ApiSideThread,
  patchSideThread,
} from "../../data/api/sideThreads";
import { Icon } from "../../icons";
import { APP_NAME } from "../../lib/appName";
import { cx } from "../../lib/cx";
import { isSideThreadUnread } from "../../lib/sideThreadReads";
import { relTime } from "../../lib/time";

export type SideThreadsListProps = {
  readonly onClose: () => void;
  readonly onOpen: (t: ApiSideThread) => void;
  readonly taskId: string;
  readonly threads: ReadonlyArray<ApiSideThread>;
};

type Order = "newest" | "oldest";

// The drawer's list is a fixed reading order — the order you started each
// thread in, not `last_activity_at` (side-threads.md §UX: a thread's spot
// never moves just because you replied to it). Newest-first by default; the
// toggle only changes which end you start reading from.
const sortThreads = (
  threads: ReadonlyArray<ApiSideThread>,
  order: Order,
): ReadonlyArray<ApiSideThread> =>
  [...threads].sort((a, b) => {
    const diff = Date.parse(a.created_at) - Date.parse(b.created_at);
    return order === "oldest" ? diff : -diff;
  });

const ThreadItem = ({
  onOpen,
  taskId,
  thread: t,
}: {
  readonly onOpen: (t: ApiSideThread) => void;
  readonly taskId: string;
  readonly thread: ApiSideThread;
}) => {
  const archived = t.archived_at !== null;
  // null = not renaming; the input holds the draft while it is. The realtime
  // `chat.side_thread.updated` refetch carries the saved title back in.
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  const saveRename = (): void => {
    if (renameDraft === null) {
      return;
    }
    const next = renameDraft.trim() || null;
    setRenameDraft(null);
    if (next !== (t.title ?? null)) {
      void patchSideThread(taskId, t.public_id, { title: next });
    }
  };
  return (
    <div className={cx(styles.item, archived && styles.itemArchived)}>
      {renameDraft !== null ? (
        <input
          autoFocus
          className={styles.itemRename}
          onBlur={saveRename}
          onChange={(e) => setRenameDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              saveRename();
            } else if (e.key === "Escape") {
              // Cancels the rename without closing the drawer around it.
              e.stopPropagation();
              setRenameDraft(null);
            }
          }}
          placeholder="Name this side thread"
          value={renameDraft}
        />
      ) : (
        <button
          className={styles.itemMain}
          onClick={() => onOpen(t)}
          type="button"
        >
          <div className={styles.itemHead}>
            {isSideThreadUnread(t) ? (
              <span className={styles.itemUnread} title="New reply" />
            ) : null}
            <span className={styles.itemTitle}>
              {t.title ?? t.anchor.body ?? "Side thread"}
            </span>
            <span className={styles.itemTs}>
              started {relTime(Date.parse(t.created_at))}
            </span>
          </div>
          {t.last_message?.body ? (
            <span className={styles.itemPreview}>
              {t.last_message.role === "agent" ? `${APP_NAME}: ` : "You: "}
              {t.last_message.body}
            </span>
          ) : null}
        </button>
      )}
      <div className={styles.itemActions}>
        <Button
          icon
          onClick={() => setRenameDraft(t.title ?? "")}
          title="Rename"
          variant="ghost"
        >
          <Icon.Pencil size={13} />
        </Button>
        <Button
          icon
          onClick={() =>
            void patchSideThread(taskId, t.public_id, { archived: !archived })
          }
          title={archived ? "Unarchive" : "Archive"}
          variant="ghost"
        >
          <Icon.Archive size={13} />
        </Button>
      </div>
    </div>
  );
};

export const SideThreadsList = ({
  onClose,
  onOpen,
  taskId,
  threads,
}: SideThreadsListProps) => {
  const [order, setOrder] = useState<Order>("newest");
  const [showArchived, setShowArchived] = useState(false);
  const active = sortThreads(
    threads.filter((t) => t.archived_at === null),
    order,
  );
  const archived = sortThreads(
    threads.filter((t) => t.archived_at !== null),
    order,
  );
  const { onHandlePointerDown, width } = usePanelWidth();
  return (
    <Dialog.Root
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      open
    >
      {/* Chat-scoped like SideThreadPanel: no portal, no scrim, and outside
          clicks dismiss. */}
      <Dialog.Content
        aria-describedby={undefined}
        className={panelStyles.panel}
        style={{ width }}
      >
        <VisuallyHidden.Root>
          <Dialog.Title>Side threads</Dialog.Title>
        </VisuallyHidden.Root>
        <div
          className={panelStyles.resizeHandle}
          onPointerDown={onHandlePointerDown}
        />

        <div className={panelStyles.head}>
          <span className={panelStyles.headIcon}>
            <Icon.Comment size={15} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={panelStyles.title}>
              {active.length} side thread{active.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className={styles.order} role="group">
            <button
              className={cx(
                styles.orderBtn,
                order === "newest" && styles.orderBtnActive,
              )}
              onClick={() => setOrder("newest")}
              type="button"
            >
              Newest first
            </button>
            <button
              className={cx(
                styles.orderBtn,
                order === "oldest" && styles.orderBtnActive,
              )}
              onClick={() => setOrder("oldest")}
              type="button"
            >
              Oldest first
            </button>
          </div>
          <Button icon onClick={onClose} variant="ghost">
            <Icon.X size={15} />
          </Button>
        </div>

        <div className={styles.list}>
          {active.map((t) => (
            <ThreadItem
              key={t.public_id}
              onOpen={onOpen}
              taskId={taskId}
              thread={t}
            />
          ))}
          {active.length === 0 ? (
            <div className={styles.emptyNote}>
              No open side threads — fork one from any message in the chat.
            </div>
          ) : null}
          {archived.length > 0 ? (
            <>
              <button
                className={styles.archivedToggle}
                onClick={() => setShowArchived(!showArchived)}
                type="button"
              >
                <Icon.ChevDown
                  size={11}
                  style={{
                    transform: showArchived ? "rotate(0)" : "rotate(-90deg)",
                    transition: "transform .15s",
                  }}
                />
                {archived.length} archived
              </button>
              {showArchived
                ? archived.map((t) => (
                    <ThreadItem
                      key={t.public_id}
                      onOpen={onOpen}
                      taskId={taskId}
                      thread={t}
                    />
                  ))
                : null}
            </>
          ) : null}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};
